import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import {
  MessageSquare, User, ChevronRight, Briefcase, Send, ArrowLeft, Search
} from 'lucide-react';
import { format } from 'date-fns';

interface Conversation {
  candidateId: string;
  candidateName: string;
  candidatePhoto: string | null;
  lastMessage: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
  jobCount: number;
  jobRoles: string[];
  applicationIds: string[];
}

interface Message {
  id: string;
  content: string;
  sender_id: string;
  created_at: string;
  application_id: string;
}

export default function Messages() {
  const { activeUserId } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedConvo, setSelectedConvo] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [search, setSearch] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeUserId) fetchConversations();
  }, [activeUserId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const { data, error } = await supabase
        .from('applications')
        .select(`
          id, created_at,
          candidate:candidates(id, name, photo_url),
          job:jobs(role, campaign:campaigns!inner(recruiter_id))
        `)
        .eq('job.campaign.recruiter_id', activeUserId!)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const candidateMap = new Map<string, Conversation>();

      for (const app of (data || []) as any[]) {
        const candidateId = app.candidate?.id;
        if (!candidateId) continue;

        const { data: msgs } = await supabase
          .from('messages')
          .select('content, created_at, sender_id, read_at')
          .eq('application_id', app.id)
          .order('created_at', { ascending: false })
          .limit(1);

        const existing = candidateMap.get(candidateId);
        if (existing) {
          existing.jobCount++;
          existing.applicationIds.push(app.id);
          if (app.job?.role && !existing.jobRoles.includes(app.job.role)) {
            existing.jobRoles.push(app.job.role);
          }
          const msgTime = msgs?.[0]?.created_at;
          if (msgTime && (!existing.lastMessageAt || new Date(msgTime) > new Date(existing.lastMessageAt))) {
            existing.lastMessage = msgs[0].content;
            existing.lastMessageAt = msgTime;
          }
          if (msgs?.[0] && !msgs[0].read_at && msgs[0].sender_id !== activeUserId!) {
            existing.unreadCount++;
          }
        } else {
          candidateMap.set(candidateId, {
            candidateId,
            candidateName: app.candidate?.name || 'Unknown',
            candidatePhoto: app.candidate?.photo_url || null,
            lastMessage: msgs?.[0]?.content || null,
            lastMessageAt: msgs?.[0]?.created_at || app.created_at,
            unreadCount: (msgs?.[0] && !msgs[0].read_at && msgs[0].sender_id !== activeUserId!) ? 1 : 0,
            jobCount: 1,
            jobRoles: app.job?.role ? [app.job.role] : [],
            applicationIds: [app.id],
          });
        }
      }

      const convos = Array.from(candidateMap.values()).sort((a, b) =>
        new Date(b.lastMessageAt || 0).getTime() - new Date(a.lastMessageAt || 0).getTime()
      );
      setConversations(convos);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const openChat = async (convo: Conversation) => {
    setSelectedConvo(convo);
    setChatLoading(true);
    try {
      const { data } = await supabase
        .from('messages')
        .select('*')
        .in('application_id', convo.applicationIds)
        .order('created_at', { ascending: true });
      setMessages(data || []);

      // Mark as read
      await supabase
        .from('messages')
        .update({ read_at: new Date().toISOString() })
        .in('application_id', convo.applicationIds)
        .neq('sender_id', activeUserId!)
        .is('read_at', null);
    } catch (err) {
      console.error('Error loading chat:', err);
    } finally {
      setChatLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConvo) return;
    try {
      const { error } = await supabase.from('messages').insert({
        application_id: selectedConvo.applicationIds[0],
        sender_id: activeUserId!,
        content: newMessage.trim(),
      });
      if (error) throw error;
      setNewMessage('');
      // Refresh messages
      const { data } = await supabase
        .from('messages')
        .select('*')
        .in('application_id', selectedConvo.applicationIds)
        .order('created_at', { ascending: true });
      setMessages(data || []);
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  const formatTime = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return format(date, 'h:mm a');
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return format(date, 'MMM d');
  };

  const filtered = conversations.filter(c =>
    c.candidateName.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-[3px] border-slate-200 border-t-slate-800 rounded-full" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden animate-in fade-in" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Left: Conversations List */}
      <div className={`${selectedConvo ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-96 border-r border-slate-200/60`}>
        <div className="p-4 border-b border-slate-100">
          <h1 className="text-lg font-black text-slate-900 tracking-tight mb-3">Messages</h1>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 w-full py-2 bg-slate-50 border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:border-slate-400 transition-all"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
              <MessageSquare size={48} className="mb-4 text-gray-300" />
              <p className="text-sm font-medium text-gray-500 text-center">No conversations yet</p>
            </div>
          ) : (
            filtered.map(convo => (
              <button
                key={convo.candidateId}
                onClick={() => openChat(convo)}
                className={`w-full flex items-center gap-3 p-4 hover:bg-blue-50/50 transition-colors text-left border-b border-gray-50 ${selectedConvo?.candidateId === convo.candidateId ? 'bg-blue-50' : ''
                  }`}
              >
                <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden">
                  {convo.candidatePhoto
                    ? <img src={convo.candidatePhoto} alt="" className="w-full h-full object-cover" />
                    : <User size={20} className="text-gray-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-gray-900 text-sm truncate">{convo.candidateName}</p>
                    <span className="text-xs text-gray-400 shrink-0 ml-2">{formatTime(convo.lastMessageAt)}</span>
                  </div>
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <Briefcase size={10} /> {convo.jobCount === 1 ? convo.jobRoles[0] : `${convo.jobCount} jobs`}
                  </p>
                  <p className="text-xs text-gray-400 truncate mt-0.5">{convo.lastMessage || '💬 Tap to start chatting...'}</p>
                </div>
                {convo.unreadCount > 0 && (
                  <span className="w-5 h-5 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center shrink-0">
                    {convo.unreadCount}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Right: Chat Panel */}
      <div className={`${selectedConvo ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
        {selectedConvo ? (
          <>
            {/* Chat Header */}
            <div className="flex items-center gap-3 p-4 border-b border-gray-100 bg-gray-50/50">
              <button onClick={() => setSelectedConvo(null)} className="md:hidden p-1 rounded-full hover:bg-gray-200 transition-colors">
                <ArrowLeft size={20} />
              </button>
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                {selectedConvo.candidatePhoto
                  ? <img src={selectedConvo.candidatePhoto} alt="" className="w-full h-full object-cover" />
                  : <User size={20} className="text-gray-400" />}
              </div>
              <div>
                <p className="font-bold text-gray-900">{selectedConvo.candidateName}</p>
                <p className="text-xs text-gray-500">{selectedConvo.jobRoles.join(', ')}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin w-8 h-8 border-[3px] border-slate-200 border-t-slate-800 rounded-full" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <MessageSquare size={40} className="mb-3 text-gray-300" />
                  <p className="text-sm">No messages yet. Say hello!</p>
                </div>
              ) : (
                messages.map(msg => (
                  <div key={msg.id} className={`flex ${msg.sender_id === activeUserId! ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-[13px] ${msg.sender_id === activeUserId!
                      ? 'bg-slate-900 text-white rounded-br-md'
                      : 'bg-slate-100 text-slate-900 rounded-bl-md'
                      }`}>
                      <p>{msg.content}</p>
                      <p className={`text-[10px] mt-1 ${msg.sender_id === activeUserId! ? 'text-white/60' : 'text-gray-400'}`}>
                        {format(new Date(msg.created_at), 'h:mm a')}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-gray-100 bg-white">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMessage()}
                  className="flex-1 bg-gray-50 border border-gray-200 rounded-full px-5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-jobisa-yellow focus:border-transparent transition-all"
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  className="w-10 h-10 bg-slate-900 text-white rounded-full flex items-center justify-center hover:bg-slate-700 disabled:opacity-50 transition-all shrink-0"
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <MessageSquare size={64} className="mb-4 text-gray-200" />
            <p className="text-lg font-medium text-gray-500">Select a conversation</p>
            <p className="text-sm text-gray-400">Choose a candidate to start chatting</p>
          </div>
        )}
      </div>
    </div>
  );
}
